/**
 * SignalingClient — thin WebSocket wrapper for /api/ws/meeting/:room_id
 *
 * Responsibilities (tight scope):
 *   • Open/close the WS, auto-derive ws/wss URL from REACT_APP_BACKEND_URL.
 *   • Subscribe pattern for inbound messages.
 *   • Fire-and-forget send().
 *   • Auto-reconnect with exponential backoff on transient drops (network
 *     blips, mobile-Safari background suspension, etc.).
 *
 * Not a peer connection (that's PeerConnection.js). Not a media manager.
 * Same channel is later used to relay proposal-sync app messages — keeps the
 * proposal layer fully decoupled from video signalling.
 */
const HTTP_BACKEND = process.env.REACT_APP_BACKEND_URL;

function wsBase() {
  if (!HTTP_BACKEND) throw new Error('REACT_APP_BACKEND_URL missing');
  return HTTP_BACKEND.replace(/^http/, 'ws');
}

const RECONNECT_INITIAL_MS = 800;
const RECONNECT_MAX_MS = 8_000;
const RECONNECT_MAX_ATTEMPTS = 8;

export class SignalingClient {
  constructor({ roomId, token, name, onMessage, onOpen, onClose, onError, onReconnect }) {
    this.roomId = roomId;
    this.token = token;
    this.name = name || '';
    this.onMessage = onMessage || (() => {});
    this.onOpen = onOpen || (() => {});
    this.onClose = onClose || (() => {});
    this.onError = onError || (() => {});
    /** Called when a reconnect succeeds — host should kick off ICE-restart. */
    this.onReconnect = onReconnect || (() => {});
    this.ws = null;
    this._closed = false;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    // Outbound buffer for messages produced before the WS is OPEN. WebRTC
    // emits signaling messages (offers, ICE candidates) from event handlers
    // that may fire before the connect handshake completes; without this
    // buffer those would silently disappear and the call would stall.
    this._sendQueue = [];
  }

  connect() {
    if (this._closed) return; // user explicitly closed; don't auto-reopen
    const params = [`token=${encodeURIComponent(this.token)}`];
    if (this.name) params.push(`name=${encodeURIComponent(this.name)}`);
    const url = `${wsBase()}/api/ws/meeting/${encodeURIComponent(this.roomId)}?${params.join('&')}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.onError(e);
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.addEventListener('open', () => {
      const wasReconnect = this._reconnectAttempts > 0;
      this._reconnectAttempts = 0;
      // Flush anything that was queued while we were still handshaking.
      // Iterate carefully — send() will push back onto the queue if the WS
      // somehow drops between flush iterations.
      const queued = this._sendQueue.splice(0);
      queued.forEach((msg) => {
        try { ws.send(JSON.stringify(msg)); } catch (e) { /* drop */ }
      });
      this.onOpen();
      if (wasReconnect) this.onReconnect();
    });
    ws.addEventListener('error', (e) => this.onError(e));
    ws.addEventListener('close', (e) => {
      // Differentiate explicit close (this._closed) vs network drop.
      if (this._closed) {
        this.onClose(e);
        return;
      }
      // Codes 4401 / 4404 / 4403 are server-side auth/business rejections —
      // never auto-retry those.
      const fatal = [4401, 4403, 4404, 4410].includes(e.code);
      if (fatal) {
        this._closed = true;
        this.onClose(e);
        return;
      }
      this.onClose(e);
      this._scheduleReconnect();
    });
    ws.addEventListener('message', (event) => {
      try {
        const msg = JSON.parse(event.data);
        this.onMessage(msg);
      } catch (err) {
        // Server only ever sends JSON; ignore malformed
      }
    });
  }

  _scheduleReconnect() {
    if (this._closed) return;
    if (this._reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      this._closed = true;
      // Surface a synthetic close so the UI shows "Connection lost"
      this.onClose({ code: 1006, reason: 'reconnect-exhausted' });
      return;
    }
    this._reconnectAttempts += 1;
    const delay = Math.min(RECONNECT_INITIAL_MS * Math.pow(2, this._reconnectAttempts - 1), RECONNECT_MAX_MS);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect();
    }, delay);
  }

  send(kind, payload, extra = null) {
    const msg = extra ? { kind, ...extra, payload } : { kind, payload };
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    // Buffer when not yet open (handshake in flight, reconnect pending, etc.)
    // — flushed on the next 'open' event. Skip ephemeral noise like 'bye'.
    if (this._closed) return false;
    if (kind === 'bye') return false;
    this._sendQueue.push(msg);
    return true;
  }

  close() {
    this._closed = true;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.send('bye', null); } catch (e) { /* noop */ }
      this.ws.close();
    }
  }
}
